import React, { useEffect } from "react";

import { ExpressEnrollmentStyledForm } from "./components";

export function ApplicationForm({ form, onErrors, formId, initialValues, isFormDisabled, children }) {
  // The modal reuses a single form instance across opens, and antd's default `preserve: true`
  // keeps the store alive through the `destroyOnClose` field remount. On reopen rc-field-form
  // merges that stale store *over* the freshly computed initialValues, so a saved draft renders
  // with empty values (e.g. the patient section) and Complete Form stays disabled. Resetting
  // re-derives the store purely from the current initialValues. (React 19 timing regression.)
  //
  // A single mount-time reset isn't enough: initialValues settles *after* mount — the modal folds
  // in async redux data, and the eligibility questionnaire (Genentech free-drug) mounts its
  // conditional required fields across several later render cycles. Those values miss a one-shot
  // reset, so the questionnaire section stays unpopulated (unselected radios → real validation
  // errors → Complete Form disabled) and never recovers. Re-derive whenever the initialValues
  // content changes, but only while the form is still pristine so we never clobber user input once
  // they've started editing.
  const initialValuesKey = JSON.stringify(initialValues);
  useEffect(() => {
    if (form.isFieldsTouched()) {
      return undefined;
    }
    form.resetFields();

    // React 19 + antd-4 regression: when a reopened draft finishes repopulating and its transient
    // required-field errors clear, rc-field-form no longer re-fires `onFieldsChange` for that cleared
    // state. The parent derives `sectionsWithErrors` (which drives the red sidebar sections and the
    // Complete Form button) only from `onFieldsChange`, so it stays stuck on the errors seen mid-mount
    // — sidebar sections stay red and Complete Form disabled even though every field is now valid.
    // Once the fields have mounted and validation has settled, report the *current* errors so the
    // parent recomputes section state from the real (settled) form instead of the stale snapshot.
    const rafId = requestAnimationFrame(() => {
      form
        .validateFields()
        .catch(() => {})
        .finally(() => onErrors(form.getFieldsError().filter((field) => field.errors.length)));
    });
    return () => cancelAnimationFrame(rafId);
  }, [initialValuesKey]);

  useEffect(() => {
    form.validateFields();
    form.setFieldsValue({ form_disabled: isFormDisabled });
  }, [isFormDisabled]);

  return (
    <ExpressEnrollmentStyledForm
      id={formId}
      form={form}
      onFieldsChange={(changedFields, allFields) => {
        const errors = allFields.filter((field) => field.errors.length);
        if (errors) {
          onErrors(errors);
        }
      }}
      layout="vertical"
      name={formId}
      initialValues={{ ...initialValues, form_disabled: isFormDisabled }}
      disabled={isFormDisabled}
    >
      {children}
    </ExpressEnrollmentStyledForm>
  );
}
